import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Building2, Unlink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Profile } from '@/types/database.types';
import { useAuditLog } from '@/hooks/use-audit-log';
import { useAuth } from '@/contexts/AuthContext';
import { AdminUserManagementService } from '@/services/admin-user-management.service';

interface PEOProvider {
  id: string;
  name: string;
}

interface PEOAssignmentSelectorProps {
  user: Profile;
  onAssignmentChange: () => void;
}

export const PEOAssignmentSelector = ({ user, onAssignmentChange }: PEOAssignmentSelectorProps) => {
  const [providers, setProviders] = useState<PEOProvider[]>([]);
  const [currentProvider, setCurrentProvider] = useState<PEOProvider | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const { logChange } = useAuditLog();
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (user.user_type === 'peo') {
      fetchProviders();
      fetchCurrentAssignment();
    }
  }, [user.id, user.user_type]);

  const fetchProviders = async () => {
    const { data, error } = await supabase
      .from('peo_providers')
      .select('id, name')
      .order('name');

    if (error) {
      console.error('Error fetching PEO providers:', error);
      return;
    }

    setProviders(data || []);
  };

  const fetchCurrentAssignment = async () => {
    const { data, error } = await supabase
      .from('peo_representatives')
      .select(`
        provider_id,
        peo_providers!inner(id, name)
      `)
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error fetching current assignment:', error);
      return;
    }

    if (data?.peo_providers) {
      setCurrentProvider({
        id: data.peo_providers.id,
        name: data.peo_providers.name
      });
    }
  };

  const assignToPEO = async (providerId: string) => {
    setIsAssigning(true);
    try {
      const provider = providers.find(p => p.id === providerId);
      
      await AdminUserManagementService.assignPEO(user.id, providerId);

      // Log the change for audit trail
      if (currentUser?.id) {
        await logChange(
          currentUser.id,
          'peo_assignment',
          currentProvider?.name || 'None',
          provider?.name || 'Unknown'
        );
      }

      toast.success('User assigned to PEO provider successfully');
      await fetchCurrentAssignment();
      onAssignmentChange();
    } catch (error) {
      console.error('Error assigning user to PEO:', error);
      toast.error('Failed to assign user to PEO provider');
    } finally {
      setIsAssigning(false);
    }
  };

  const unassignFromPEO = async () => {
    setIsAssigning(true);
    try {
      // For unassignment, we still need to use direct deletion since there's no specific function
      // But first check if user has permission
      const { error } = await supabase
        .from('peo_representatives')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      // Log the change for audit trail
      if (currentUser?.id) {
        await logChange(
          currentUser.id,
          'peo_assignment',
          currentProvider?.name || 'Unknown',
          'None'
        );
      }

      toast.success('User unassigned from PEO provider');
      setCurrentProvider(null);
      onAssignmentChange();
    } catch (error) {
      console.error('Error unassigning user from PEO:', error);
      toast.error('Failed to unassign user from PEO provider');
    } finally {
      setIsAssigning(false);
    }
  };

  if (user.user_type !== 'peo') {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">PEO Assignment</span>
      </div>

      {currentProvider ? (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="flex-1">
            {currentProvider.name}
          </Badge>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" disabled={isAssigning}>
                <Unlink className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Unassign PEO Provider</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to unassign {user.first_name} {user.last_name} from {currentProvider.name}? 
                  This will remove their access to PEO provider resources and quotes.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={unassignFromPEO}>Unassign</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      ) : (
        <Select onValueChange={assignToPEO} disabled={isAssigning}>
          <SelectTrigger>
            <SelectValue placeholder="Select PEO Provider" />
          </SelectTrigger>
          <SelectContent>
            {providers.map((provider) => (
              <SelectItem key={provider.id} value={provider.id}>
                {provider.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
};
